import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsApiService } from '../api/settings-api';
import { UpdateSettingsDTO } from '../types/settings.types';
import { Settings } from '../types/settings.types';

// Estado inicial
interface SettingsState {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
}

const initialState: SettingsState = {
  settings: null,
  loading: false,
  error: null,
  saving: false,
};

// Async Thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApiService.getSettings();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar configuración'
      );
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (updateDto: UpdateSettingsDTO, { rejectWithValue }) => {
    try {
      const response = await settingsApiService.updateSettings(updateDto);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar configuración'
      );
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSettings: (state) => {
      state.settings = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update settings
    builder
      .addCase(updateSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetSettings } = settingsSlice.actions;

// Selectors
export const selectSettings = (state: { settings: SettingsState }) => state.settings.settings;
export const selectSettingsLoading = (state: { settings: SettingsState }) => state.settings.loading;
export const selectSettingsSaving = (state: { settings: SettingsState }) => state.settings.saving;
export const selectSettingsError = (state: { settings: SettingsState }) => state.settings.error;

export default settingsSlice.reducer;

